import { useCallback, useEffect, useMemo, useState } from "react";
import { AnyVariables } from "urql";

import { ErrorWithDataFragment } from "../queries.graphql";
import useOnValueChanged from "../useOnValueChanged";
import {
  useQueryWithResponseHandler,
  UseQueryWithResponseHandlerQueryArgs,
} from "../useQueryWithResponseHandler";
import { ResponseData } from "../useUrqlResponseHandler";

export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  endCursor?: string | null;
  startCursor?: string | null;
  totalCount?: number;
}

export const EmptyPageInfo: PageInfo = {
  hasNextPage: false,
  hasPreviousPage: false,
  endCursor: null,
  startCursor: null,
};

export type UseInfinitePaginationProps<
  Data extends Record<string, any>,
  Variables extends AnyVariables,
  Item,
> = UseQueryWithResponseHandlerQueryArgs<Variables, Data> & {
  applyPagination: (vars: Variables, after: string | null) => Variables;
  extractQueryItems: (result: ResponseData<Data>) => {
    pageInfo: PageInfo;
    items: Array<Item>;
  };
};

export const useInfinitePagination = <
  Data extends Record<string, any>,
  Variables extends AnyVariables,
  Item,
>(
  props: UseInfinitePaginationProps<Data, Variables, Item>,
): {
  items: Array<Item>;
  pageInfo: PageInfo;
  error: ErrorWithDataFragment | null | undefined;
  execute: () => void;
  fetchMore: () => boolean;
  isFetching: boolean;
} => {
  const { applyPagination, extractQueryItems, query, variables, ...rest } =
    props;

  const [after, setAfter] = useState<string | null>(null);
  const [isReloading, setIsReloading] = useState(false);

  const appliedVariables = useMemo(
    () => applyPagination(variables as Variables, after),
    [variables, after, applyPagination],
  );

  const { state, execute, data, error } = useQueryWithResponseHandler({
    query,
    variables: appliedVariables,
    ...rest,
  });

  // Reset pagination when queryArgs change; force re-execute when not paginated
  useOnValueChanged([query, variables], () => {
    setAfter(null);
    setIsReloading(true);
    // When after is already null, setAfter is a no-op (no re-render).
    // Force urql to re-execute with the new variables.
    if (after === null) {
      execute();
    }
  });

  // Clear isReloading when the new query settles with data
  useEffect(() => {
    if (isReloading && state.data && !state.fetching && !state.stale) {
      setIsReloading(false);
    }
  }, [isReloading, state.data, state.fetching, state.stale]);

  const { items, pageInfo } = useMemo(() => {
    return extractQueryItems(data);
  }, [data, extractQueryItems]);

  const fetchMore = useCallback(() => {
    if (pageInfo.endCursor && pageInfo.hasNextPage) {
      setAfter(pageInfo.endCursor);
      return true;
    }
    return false;
  }, [pageInfo.endCursor, pageInfo.hasNextPage]);

  const reload = useCallback(() => {
    setAfter(null);
    execute();
  }, [execute]);

  return {
    items: isReloading ? [] : items,
    pageInfo: isReloading ? EmptyPageInfo : pageInfo,
    error,
    execute: reload,
    fetchMore,
    isFetching:
      isReloading ||
      state.fetching ||
      state.stale ||
      (!state.data && !props.pause),
  };
};
